/**
 * The version bar (DESIGN UD-27, `docs/plan/VERSION-CHAIN-DESIGN.md` §5): above the views once the document has a version. It
 * lists the original and every version (what made it, when), marks the latest and the one on screen, and has View, Undo last
 * step and Start again from the original (which asks once, inline). Text nodes only.
 */
import { signal } from '@preact/signals';
import { useState } from 'preact/hooks';
import { result } from '../state.ts';
import { LATEST, REVIEW, VERSIONS } from '../strings-bench.ts';
import { HEADER, WORKING } from '../strings.ts';
import { Dialog, Notice } from '../ui.tsx';
import { beginWork, endWork, nextPaint } from '../work.ts';
import { openMarkdownReview } from './MarkdownReview.tsx';
import { withGuardrailsLabel } from './action-label.ts';
import {
	dismissAsk,
	documentBusy,
	markupHints,
	origin,
	reanalyseLatest,
	reopenAsk,
	restoreVersions,
	showLatest,
	showVersion,
	startAgain,
	startFromOriginal,
	undoLastStep,
	versionText,
} from './chain-control.ts';
import type { ChainVersion } from './chain.ts';
import { fmtJobTime } from './jobs-format.ts';
import {
	chain,
	jobs,
	latestN,
	liveJobId,
	versionError,
	versionWork,
	versionsAsk,
	versionsAskOpen,
	viewing,
} from './state.ts';
import { downloadVersion } from './version-export.ts';

/** On a phone the bar starts collapsed to its one-line summary; the choice is made once, when the page loads. */
const startsOpen = typeof matchMedia !== 'function' || !matchMedia('(max-width: 767px)').matches;

/** What made a version, in words. */
function madeBy(v: ChainVersion): string {
	const m = v.made;
	if (m.kind === 'local') return VERSIONS.madeByLocal(String(m.changes));
	const s = VERSIONS.scopes;
	let label: string;
	switch (m.scope.kind) {
		case 'channel':
			label = s.channel(m.scope.id);
			break;
		case 'hotspot':
			label = s.hotspot(m.scope.channel);
			break;
		default:
			label = s[m.scope.kind];
	}
	// a run made with the editorial guardrails says so (ED-191)
	return withGuardrailsLabel(label, m.guardrails);
}

const downloading = signal<number | null>(null);
const downloadError = signal<string | null>(null);

/** "Download (Markdown)": version `n` as Markdown with the upload's structure (UD-30). */
async function download(n: number, label: string): Promise<void> {
	const o = origin();
	const text = versionText(n);
	if (!o || text === null || downloading.peek() !== null) return;
	downloading.value = n;
	downloadError.value = null;
	const id = `version-download-${n}`;
	beginWork(id, WORKING.ops.rewriteExport(WORKING.formats.md));
	try {
		await nextPaint();
		await downloadVersion(o.meta, text, 'md', n === 0 ? 'original' : { version: n }, label);
	} catch (e) {
		downloadError.value = HEADER.exportFailed((e as Error).message);
	} finally {
		endWork(id);
		downloading.value = null;
	}
}

/** "Review the Markdown" for a version (UD-31), with the number of spans still to check once a download or review has counted them. */
function ReviewButton({ n, v, label }: { n: number; v: ChainVersion; label: string }) {
	const hint = markupHints.value[v.hash];
	const open = hint ? hint.moved + hint.dropped : 0;
	return (
		<button
			type="button"
			class="btn btn-secondary btn-sm"
			data-testid={`version-review-${n}`}
			aria-label={REVIEW.openLabel(label)}
			title={REVIEW.openHelp}
			onClick={() => void openMarkdownReview(n)}
		>
			{REVIEW.open}
			{open > 0 ? (
				<span class="version-badge version-badge-warn" data-testid={`version-review-badge-${n}`}>
					{REVIEW.badge(String(open))}
				</span>
			) : null}
		</button>
	);
}

function VersionItem({ n, v }: { n: number; v: ChainVersion | null }) {
	const on = viewing.value === n;
	const label = n === 0 ? VERSIONS.original : VERSIONS.version(String(n));
	return (
		<li class="version-item" data-testid="version-item" data-n={n} data-on-screen={on}>
			<span class="version-name">{label}</span>
			{v ? (
				<span class="version-made">
					{madeBy(v)} ·{' '}
					<time dateTime={v.createdAt} data-testid="version-time">
						{fmtJobTime(v.createdAt)}
					</time>
				</span>
			) : null}
			{n === latestN.value ? <span class="version-badge">{VERSIONS.latest}</span> : null}
			{on ? <span class="version-badge version-badge-on">{VERSIONS.onScreen}</span> : null}
			{on ? null : (
				<button
					type="button"
					class="btn btn-secondary btn-sm"
					data-testid={`version-view-${n}`}
					aria-label={`${VERSIONS.view} ${label}`}
					onClick={() => void showVersion(n)}
				>
					{VERSIONS.view}
				</button>
			)}
			<button
				type="button"
				class="btn btn-secondary btn-sm"
				data-testid={`version-download-${n}`}
				aria-label={LATEST.downloadLabel(label)}
				disabled={downloading.value !== null}
				onClick={() => void download(n, label)}
			>
				{LATEST.download}
			</button>
			{v ? <ReviewButton n={n} v={v} label={label} /> : null}
		</li>
	);
}

/** The saved versions of a document opened again wait for an answer (DESIGN UD-28): restore them, or start from the original. */
function VersionsAsk({ root }: { root: string }) {
	const c = chain.value;
	const last = c?.versions.at(-1);
	if (!c || c.root !== root || !last) return null;
	const n = String(c.versions.length);
	const busy = documentBusy(root);
	const A = VERSIONS.ask;
	return (
		<section class="version-bar" aria-label={VERSIONS.barLabel} data-testid="version-ask">
			<p data-testid="version-ask-bar">{A.bar(n)}</p>
			<div class="version-actions">
				<button
					type="button"
					class="btn btn-primary btn-sm"
					data-testid="version-ask-choose"
					onClick={reopenAsk}
				>
					{A.choose}
				</button>
			</div>
			<Dialog
				title={A.title}
				open={versionsAskOpen.value}
				onClose={dismissAsk}
				actions={
					<>
						<button
							type="button"
							class="btn btn-secondary"
							data-testid="version-ask-original"
							disabled={busy}
							onClick={() => void startFromOriginal()}
						>
							{A.original}
						</button>
						<button
							type="button"
							class="btn btn-primary"
							data-testid="version-ask-restore"
							onClick={() => void restoreVersions()}
						>
							{A.restore(n)}
						</button>
					</>
				}
			>
				<p data-testid="version-ask-body">
					{A.body(n, VERSIONS.version(String(last.n)), madeBy(last), fmtJobTime(last.createdAt))}
				</p>
				{busy ? <p class="dim">{A.busy}</p> : null}
			</Dialog>
		</section>
	);
}

export function VersionBar() {
	const ask = versionsAsk.value;
	const c = chain.value;
	const work = versionWork.value;
	const error = versionError.value;
	const [confirming, setConfirming] = useState(false);
	if (ask && c?.root === ask) return <VersionsAsk root={ask} />;
	if ((!c || c.versions.length === 0) && !work && !error) return null;
	const latest = latestN.value;
	const on = viewing.value;
	const live = liveJobId.value;
	const liveBase = live ? jobs.value.find((j) => j.id === live)?.base : undefined;
	return (
		<section
			class="version-bar"
			aria-label={VERSIONS.barLabel}
			data-testid="version-bar"
			data-latest={latest}
			data-viewing={on}
		>
			<details open={startsOpen}>
				<summary>
					{VERSIONS.barLabel} · {on === 0 ? VERSIONS.original : VERSIONS.version(String(on))}
					{on === latest ? ` (${VERSIONS.latest})` : ''}
				</summary>
				<ul class="version-list" data-testid="version-list">
					<VersionItem n={0} v={null} />
					{(c?.versions ?? []).map((v) => (
						<VersionItem key={v.n} n={v.n} v={v} />
					))}
				</ul>
				<p class="dim version-showing" data-testid="version-showing">
					{latest === 0 ? VERSIONS.showingOriginal : VERSIONS.showing(String(on), String(latest))}
				</p>
				{liveBase ? (
					<p class="dim" data-testid="version-running">
						{VERSIONS.runningOn(String(liveBase.n))}
					</p>
				) : null}
				{work ? (
					<div class="version-analysing" data-testid="version-analysing">
						<p class="dim">{VERSIONS.analysingNote(String(work.n))}</p>
						<progress
							class="job-bar"
							value={work.fraction}
							max={1}
							aria-label={VERSIONS.analysing(String(work.n))}
						/>
					</div>
				) : null}
				{error ? (
					<div data-testid="version-error">
						<Notice kind="error">{error}</Notice>
						<button
							type="button"
							class="btn btn-secondary btn-sm"
							data-testid="version-retry"
							onClick={() => void showVersion(on)}
						>
							{VERSIONS.retry}
						</button>
					</div>
				) : null}
				{downloadError.value ? <Notice kind="error">{downloadError.value}</Notice> : null}
				<div class="version-actions">
					<button
						type="button"
						class="btn btn-primary btn-sm"
						data-testid="version-reanalyse"
						title={LATEST.reanalyseHelp}
						disabled={!!work || !result.value}
						onClick={() => void reanalyseLatest()}
					>
						{LATEST.reanalyse}
					</button>
					{on !== latest ? (
						<button
							type="button"
							class="btn btn-primary btn-sm"
							data-testid="version-view-latest"
							onClick={() => void showLatest()}
						>
							{VERSIONS.viewLatest}
						</button>
					) : null}
					<button
						type="button"
						class="btn btn-secondary btn-sm"
						data-testid="version-undo"
						disabled={latest === 0}
						title={VERSIONS.undoHelp}
						onClick={() => {
							setConfirming(false);
							void undoLastStep();
						}}
					>
						{VERSIONS.undo}
					</button>
					<button
						type="button"
						class="btn btn-secondary btn-sm"
						data-testid="version-start-again"
						disabled={latest === 0}
						onClick={() => setConfirming(true)}
					>
						{VERSIONS.startAgain}
					</button>
				</div>
				{confirming && latest > 0 ? (
					<div class="version-confirm" data-testid="version-start-again-confirm">
						<p>{VERSIONS.startAgainConfirm(String(latest))}</p>
						<div class="version-actions">
							<button
								type="button"
								class="btn btn-primary btn-sm"
								data-testid="version-start-again-yes"
								onClick={() => {
									setConfirming(false);
									void startAgain();
								}}
							>
								{VERSIONS.startAgainYes}
							</button>
							<button
								type="button"
								class="btn btn-secondary btn-sm"
								data-testid="version-start-again-no"
								onClick={() => setConfirming(false)}
							>
								{VERSIONS.startAgainNo}
							</button>
						</div>
					</div>
				) : null}
			</details>
		</section>
	);
}
